import clsx from "clsx";
import type React from "react";
import type { ReactNode } from "react";
import { Text } from "../Text";
import styles from "./Input.module.scss";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  helperText?: string;
  inputSize?: "small" | "medium" | "large";
  leftComponent?: ReactNode;
  rightComponent?: ReactNode;
  labelClassName?: string;
  inputContainerClassName?: string;
  inputWrapperClassName?: string;
  onRightIconClick?: () => void;
  rightIconAriaLabel?: string;
  errors?: string[];
}

export const Input: React.FC<InputProps> = ({
  label,
  helperText,
  inputSize = "medium",
  id,
  leftComponent,
  rightComponent,
  labelClassName,
  inputContainerClassName,
  inputWrapperClassName,
  onRightIconClick,
  rightIconAriaLabel,
  errors,
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = errors && errors.length > 0;

  const containerClassName = clsx(
    styles.inputContainer,
    inputSize && styles[inputSize],
    leftComponent && styles.hasLeftComponent,
    inputContainerClassName,
    hasError && styles.error
  );

  return (
    <div className={clsx(styles.inputWrapper, inputWrapperClassName)}>
      {label && (
        <Text
          variant="small"
          as="label"
          className={clsx(styles.label, labelClassName)}
          htmlFor={inputId}
        >
          {label}
        </Text>
      )}
      <div className={containerClassName}>
        {leftComponent && (
          <div className={styles.icon} data-side="left">
            {leftComponent}
          </div>
        )}
        <input data-testid="input" id={inputId} {...props} />
        {rightComponent && (
          <button
            type="button"
            className={styles.icon}
            data-side="right"
            onClick={onRightIconClick}
            aria-label={rightIconAriaLabel || "Toggle action"}
          >
            {rightComponent}
          </button>
        )}
      </div>
      {helperText && !hasError && (
        <Text className={styles.helperText} variant="xsmall">
          {helperText}
        </Text>
      )}
      {errors?.map((error) => (
        <Text key={error} className={styles.errorMessage} variant="xsmall">
          {error}
        </Text>
      ))}
    </div>
  );
};
